import React, { useState, useEffect, useContext, useRef } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { listFiles, createFolder, getDownloadUrl, getUploadUrl } from '../services/api';
import { PathContext } from '../contexts/PathContext';

const DownloadIcon = ({ size = 20, color = '#2563eb' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width={size}
    height={size}
  >
    {/* Arrow down */}
    <path d="M12 3v12m0 0l4-4m-4 4l-4-4" />
    {/* Box */}
    <rect x="3" y="17" width="18" height="4" rx="1" ry="1" />
  </svg>
);

// Refresh icon SVG
const RefreshIcon = ({ size = 24, color = '#fff' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width={size}
    height={size}
  >
    <path d="M4 4v6h6" />
    <path d="M20 20v-6h-6" />
    <circle cx="12" cy="12" r="8" />
  </svg>
);

export default function FileBrowser({ onSignOut }) {
  const { route } = useAuthenticator();
  const { path, setPath } = useContext(PathContext);

  const [token, setToken] = useState();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [newFolder, setNewFolder] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [refreshCounter, setRefreshCounter] = useState(0); // <-- ADDED

  const fileInputRef = useRef();

  useEffect(() => {
    if (route === 'authenticated') {
      fetchAuthSession()
        .then(session => {
          const idToken = session.tokens?.idToken?.toString();
          setToken(idToken);
        })
        .catch(err => {
          setToken(undefined);
          console.error('Failed to retrieve Cognito session:', err);
        });
    } else {
      setToken(undefined);
    }
  }, [route]);

  useEffect(() => {
    async function fetchData() {
      if (route !== 'authenticated' || !token) {
        setFolders([]);
        setFiles([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const data = await listFiles(path);
        const payload = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setFolders(payload.folders || []);
        setFiles(payload.files || []);
      } catch (err) {
        setError(err.message || 'Failed to load files');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [path, token, route, refreshCounter]); // <-- Added refreshCounter here

  // Updated refresh to trigger a refreshCounter increment
  const refresh = () => {
    setSuccess(null);
    setError(null);
    setRefreshCounter(c => c + 1);
  };

const enterFolder = folder => {
   // build a path *relative* to the user’s base folder
   // if already in a sub-path, append; otherwise start fresh
   const newPath = path
     ? `${path}/${folder.name}`
     : folder.name;
   setPath(newPath);
 };

const handleDownload = async (item) => {
  if (route !== 'authenticated' || !token) {
    setError('User not authenticated');
    return;
  }

  try {
    console.log('Downloading key:', item.key);
    const response = await getDownloadUrl(item.key, token);
    const url = response.url || (response.body && JSON.parse(response.body).url);
    if (!url) throw new Error('Failed to get download URL');

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Download failed with status ${res.status}`);

    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = item.name || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    setError(err.message || 'Failed to download file');
  }
};


  const handleCreateFolder = async () => {
    if (!newFolder.trim() || route !== 'authenticated' || !token) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await createFolder(path || '', newFolder.trim(), token);
      setNewFolder('');
      setSuccess(`Folder "${newFolder.trim()}" uploaded successfully!`);
      refresh();
    } catch (err) {
      setError(err.message || 'Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async e => {
    if (!e.target.files.length) return;
    if (route !== 'authenticated' || !token) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      for (const file of e.target.files) {
        const uploadUrlResponse = await getUploadUrl(path || '', file.name, token);
        const uploadUrl = uploadUrlResponse.uploadUrl;

        if (!uploadUrl) {
          throw new Error('Failed to get upload URL');
        }

        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });

        if (!res.ok) {
          throw new Error(`Failed to upload file: ${file.name} (status ${res.status})`);
        }
      }
      setSuccess('File(s) uploaded successfully!');
      refresh();
    } catch (err) {
      setError(err.message || 'Failed to upload file(s)');
    } finally {
      setLoading(false);
    }
  };

  if (route !== 'authenticated') {
    return (
      <div
        style={{
          height: '100vh',
          backgroundColor: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#374151',
          fontSize: '1.2rem',
          fontWeight: 'bold',
          userSelect: 'none',
        }}
      >
        Logged Out Successfully!
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#f9fafb',
        color: '#374151',
        borderRadius: 8,
        padding: 20,
        boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
      }}
    >
      {/* Error message */}
      {error && (
        <div
          style={{
            color: '#b91c1c',
            backgroundColor: '#fee2e2',
            padding: '8px 16px',
            borderRadius: 4,
            marginBottom: 16,
            fontWeight: '600',
          }}
          role="alert"
        >
          Error: {error}
        </div>
      )}

      {/* Success message */}
      {success && (
        <div
          style={{
            color: '#237804',
            backgroundColor: '#f6ffed',
            padding: '8px 16px',
            borderRadius: 4,
            marginBottom: 16,
            fontWeight: '600',
          }}
          role="alert"
        >
          {success}
        </div>
      )}

      {/* Top controls with Refresh icon */}
      <div
        style={{
          marginBottom: 20,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={refresh}
          disabled={loading}
          aria-label="Refresh"
          title="Refresh"
          style={{
            backgroundColor: '#2563eb',
            border: 'none',
            padding: '8px 12px',
            borderRadius: 6,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2563eb')}
        >
          <RefreshIcon size={20} color="#fff" />
        </button>

        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
            flexGrow: 1,
            justifyContent: 'flex-end',
          }}
        >
          <input
            type="text"
            placeholder="New folder name"
            value={newFolder}
            onChange={e => setNewFolder(e.target.value)}
            style={{
              padding: 10,
              borderRadius: 6,
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#374151',
              fontSize: '1rem',
              minWidth: 180,
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
            disabled={loading}
          />
          <button
            type="button"
            onClick={handleCreateFolder}
            disabled={loading}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2563eb')}
          >
            Create Folder
          </button>

          <button
            onClick={handleUploadClick}
            disabled={loading}
            style={{
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 6,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#15803d')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#16a34a')}
          >
            Upload File
          </button>

          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      {/* ... rest of your table code unchanged ... */}
      <table
        border="0"
        cellPadding="8"
        cellSpacing="0"
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0 8px',
          color: '#374151',
        }}
      >
        <thead>
          <tr style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            <th
              style={{
                textAlign: 'left',
                paddingLeft: 12,
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9fafb',
                zIndex: 2,
                borderBottom: '1px solid #e5e7eb',
                borderTopLeftRadius: 8,
              }}
            >
              Name
            </th>
            <th
              style={{
                textAlign: 'right',
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9fafb',
                zIndex: 2,
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              Size
            </th>
            <th
              style={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9fafb',
                zIndex: 2,
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              Last Modified
            </th>
            <th
              style={{
                textAlign: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: '#f9fafb',
                zIndex: 2,
                borderBottom: '1px solid #e5e7eb',
                borderTopRightRadius: 8,
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: 20 }}>
                Loading files…
              </td>
            </tr>
          )}

          {!loading && folders.length === 0 && files.length === 0 && (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: 20 }}>
                No items
              </td>
            </tr>
          )}

            {!loading &&
            folders.map(f => (
              <tr
                key={`folder-${f.key}`}
                style={{
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  borderRadius: 6,
                  boxShadow: '0 1px 3px rgb(0 0 0 / 0.1)',
                  transition: 'background-color 0.15s',
                }}
                onClick={() => enterFolder(f)}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#fff')}
              >
                <td style={{ paddingLeft: 16, fontWeight: '600', color: '#2563eb' }}>
                  📁 {f.name}
                </td>
                <td style={{ textAlign: 'right' }}>-</td>
                <td>-</td>
                <td style={{ textAlign: 'center' }}>{/* No actions */}</td>
              </tr>
            ))}

          {!loading &&
            files.map(f => (
              <tr
                key={`file-${f.key}`}
                style={{
                  backgroundColor: '#fff',
                  borderRadius: 6,
                  boxShadow: '0 1px 3px rgb(0 0 0 / 0.1)',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#fff')}
              >
                <td style={{ paddingLeft: 16 }}>📄 {f.name}</td>
                <td style={{ textAlign: 'right' }}>{f.size.toLocaleString()} bytes</td>
                <td>{new Date(f.lastModified).toLocaleString()}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleDownload(f);
                    }}
                    title="Download"
                    aria-label={`Download ${f.name}`}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 10px',
                      color: '#2563eb',
                      transition: 'color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#1d4ed8')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#2563eb')}
                  >
                    <DownloadIcon />
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}